// Partner visits API (2026-10-06): see docs/superpowers/specs/2026-10-06-partner-visits-api-design.md

import { h, render, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { IntlProvider, Text, Localizer, withText } from 'preact-i18n';
import { route } from 'preact-router';
import Markdown from 'preact-markdown';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';
import { diffJournalForSave, journalHasUserContent } from '../../lib/journalSaveDiff';
import { buildJournalTemplateFields } from '../../lib/textTemplateFields';
import { hasClientAdvice } from '../../lib/journalAdvice';
import { printViaIframe } from '../../lib/printHelper';
import { saveJournalAnimalChipId, stageJournalAnimalChipId } from '../../lib/journalAnimalChipId';
import { planJournalPaste } from '../../lib/journalPasteSplit';
import PubSub, { topics } from '../../lib/pubsub';
import SyncBanner from '../../components/gui/syncBanner';

import FormFields from '../../components/form/fields';
import FormFieldsCompact from '../../components/form/fieldsCompact';
import Input from '../../components/form/input';
import DyrejournalAnimalListMini from '../../components/dyrejournal/listAnimalMini';
import DyrejournalPrescriptionListMini from '../../components/dyrejournal/listPrescriptionMini';
import DyrejournalJournalListMini from '../../components/dyrejournal/listJournalMini';
import IdexxOrderStatus from '../../components/dyrejournal/idexxOrderStatus';
import DyrejournalLabResultsListMini from '../../components/dyrejournal/listLabResultsMini';
import PartnerLaunchButtons from './partnerLaunchButtons';
import PartnerSuggestionsBanner from './partnerSuggestionsBanner';
import DentalChartSection from '../../components/dyrejournal/dentalChartSection';
import Versions from '../../components/versions';
import DyrejournalJournalPrint from '../../components/dyrejournal/journalPrint';
import DyrejournalLinkJournalToSale from '../../components/dyrejournal/linkJournalToSale';
import JournalFeedback from '../../components/dyrejournal/journalFeedback';
import DyreIdReportBatchButton from '../../components/dyrejournal/dyreIdReportBatchButton';
import ConsultationRecorder from './consultationRecorder';

import ImageScroller from '../../components/imagescroller';
import LineGraphMini from '../../components/lineGraph/mini';

import definitionNo from '../../languages/no.json';
import definitionEn from '../../languages/en.json';
import { appConfirm } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';
import { animalAge } from '../../lib/animalAge';

const countryMap = {
    no: definitionNo,
    en: definitionEn,
    default: definitionEn,
};

function sortByDateAsc(a, b) {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return dateA - dateB;
}

@withText(props => ({
    switchClinicConfirm: <Text id='dyrejournal.journal-switch-clinic-confirm'>This sale is registered on another clinic. Switch to that clinic to open the sale?</Text>,
}))
@observer
class NewJournal extends Component {
	constructor(props) {
        super(props);
        this.state = {
            sections: fields.get('journal', 'Journal basics'),
            sectionsMedical: fields.get('journal', 'Journal medical'),
            sectionsClinical: fields.get('journal', 'Journal clinical'),
            sectionsPassport: fields.get('journal', 'Journal passport'),
            sectionsExtra: fields.get('journal', 'Journal extra'),
            sectionsMedia: fields.get('journal', 'Journal media'),
            showOtherJournals: false,
            syncJournalNotification: null,
            showCloseConfirm: false,
        };
        this.searchTimer = null;
        this.syncJournalSubscription = null;
        this.animalChipIdSaveTimer = null;
        this.pendingAnimalChipId = null;
        this.animalChipIdSavePromise = Promise.resolve();
    }

    toggleWeightView = async () => {
        const { userStore } = this.props.stores;
        const { user } = userStore;
        const { settings = {} } = user;
        const current = settings.weightViewMode || 'graph';
        const newValue = current === 'graph' ? 'table' : 'graph';
        await userStore.updateSetting(
            { 'settings.weightViewMode': newValue },
            { weightViewMode: newValue },
        );
    }

    gotoLinkedSale = async () => {
        const dialogAction = beginDialogAction(this, 'gotoLinkedSale', () => [
            this.props.stores.journalStore.newJournal?.id,
        ]);
        if (!dialogAction) return false;
        try {
            // Open the sale linked to this journal on the sales page (regningsfelt), with the
            // owner + animal already loaded, ready for product lines / payment. Returns true
            // when it navigated, false otherwise (no linked sale, or a clinic switch declined).
            const { appState, userStore, saleStore, journalStore, visitorStore, animalStore, calendarStore } = this.props.stores;
            const { newJournal = {} } = journalStore;
            const saleId = parseInt(newJournal.sale, 10);
            const journalCustomerId = parseInt(newJournal.customer, 10);
            if (!saleId) {
                return false;
            }
            const { switchClinicConfirm } = this.props;
            const activeCustomerId = userStore.customer?.id;

            // Sales are scoped to a single clinic, but the journal view spans every clinic
            // the user has access to. If this sale belongs to another clinic than the
            // active one, confirm and switch clinic first so the sale — and any payment
            // registered on it — lands on the correct clinic.
            if (journalCustomerId && activeCustomerId && journalCustomerId !== activeCustomerId) {
                const clinicName = (userStore.currentCustomers || []).find(c => c.id === journalCustomerId)?.name || '';
                const message = clinicName ? `${switchClinicConfirm}\n\n${clinicName}` : `${switchClinicConfirm}`;
                if (!((await appConfirm(message)) && dialogAction.isCurrent())) {
                    return false;
                }
                // Clear cached data, then switch the active clinic (same flow as the clinic
                // switcher in the menu) before navigating to the sale.
                saleStore?.clearAll();
                journalStore?.clearAll();
                visitorStore?.clearAll();
                animalStore?.clearAll();
                calendarStore?.clearAll();
                await userStore.save({ customer: journalCustomerId });
                await userStore.getInfo({ forceLoad: true });
            }

            // Close the whole right-drawer stack — the journal can be open at level 2-5,
            // and only level 1 cascades all levels closed and clears the noscroll lock.
            appState.toggleDrawerRight(false, 1);
            route(`/dyrejournal-sale/${saleId}`);
            return true;
        } finally {
            dialogAction.finish();
        }
    }

    onClickGotoSale = async e => {
        // Open the sale linked to this journal on the sales page, ready for payment.
        e.stopPropagation();
        e.preventDefault();
        await this.gotoLinkedSale();
    }

    loadAll = async (props = this.props, emptyFirst = true) => {
        const { userStore, saleStore, journalStore } = this.props.stores;
        const { id, isNew, animals = [] } = props;
// console.log('loadAll', { animals });
        journalStore.createNewJournal();
        // Load active journals for active animals.
        // const { newSale } = saleStore;
        // const { animals } = newSale;
        const { newJournal = {} } = journalStore;
        await this.loadJournal(id, isNew);
        await journalStore.loadVeterinaries();
        if (isNew) {
            const animalsIds = animals.map(animal => animal.id);
            await this.loadJournals(animalsIds, id);
        }
        if (isNew) {
            const { user } = userStore;
            if (!newJournal.veterinary) {
                journalStore.updateObjectKeyValue('newJournal', 'veterinary', user.id);
            }
            // TICKET-01430: bring back text typed into a journal that was never created
            // server-side — a drawer remount, reload or tab crash wipes the shared
            // newJournal observable, and id-less journals have no autosave. The store
            // only restores a draft mirrored for this exact sale+animal.
            this.setState({ restoredDraftSavedAt: journalStore.restoreNewJournalDraft() });
        }
    }

    loadJournal = async (id, isNew) => {
        const { saleStore, journalStore, animalStore, calendarEventStore, visitorStore, appState } = this.props.stores;
        const { animals = [], calendarEventId } = this.props;
        // Empty all stores first
        journalStore.updateKeyValue('journal', {});
        journalStore.clearPartnerSuggestions();
        // journalStore.updateKeyValue('journals', []);
        animalStore.updateKeyValue('animal', {});
        animalStore.updateKeyValue('animals', []);
        visitorStore.updateKeyValue('visitor', {});
        visitorStore.updateKeyValue('visitors', []);

        if (isNew) {
            if (calendarEventId) {
                const currentCalendarEvents = await calendarEventStore.load(calendarEventId, false, { skipUpdate: true });
                const currentCalendarEvent = currentCalendarEvents[0];
                if (currentCalendarEvent && currentCalendarEvent.title) {
                    const { newJournal } = journalStore;
                    const { chiefComplaint } = newJournal;
                    journalStore.updateObjectKeyValue('newJournal', 'chiefComplaint', `${chiefComplaint ? `${chiefComplaint} ` :  ''}${currentCalendarEvent.title} ${currentCalendarEvent.body}`);
                }
            }
            const { newSale } = saleStore;
            const { visitors } = newSale;
            // A sale created from an online prescription order carries the owner's request
            // in its description; start the anamnesis with it so the vet does not have to
            // copy it over by hand (TICKET-01849).
            if (newSale.prescriptionOrder > 0 && newSale.description) {
                const { chiefComplaint } = journalStore.newJournal;
                journalStore.updateObjectKeyValue('newJournal', 'chiefComplaint', `${chiefComplaint ? `${chiefComplaint}\n` : ''}${newSale.description}`);
            }
            // When the saleDateTop feature is on, the journal should inherit the
            // sale's visit date (so back-dated visits don't get today's date).
            const journalDate = (saleStore.saleDateTopEnabled && newSale.saleDate) ? newSale.saleDate : new Date();
            journalStore.updateObjectKeyValue('newJournal', 'date', journalDate);
            if (newSale.id) {
                journalStore.updateObjectKeyValue('newJournal', 'sale', newSale.id);
            }
            if (visitors.length > 0) {
                const visitorId = visitors[0].id;
                journalStore.updateObjectKeyValue('newJournal', 'visitor', visitorId);
                // Always load the visitor fresh from the API when opening a journal. A journal
                // can be opened from many different places, so we must NOT rely on passed-in or
                // embedded sale data — load it. This populates visitorStore.visitor, which the
                // header reads.
                if (visitorId) {
                    await visitorStore.load(visitorId);
                }
            }
            if (animals.length > 0) {
                const animalId = animals[0].id;
                journalStore.updateObjectKeyValue('newJournal', 'animal', animalId);
                // Always load the animal fresh from the API when opening a journal — same reason
                // as the visitor above, and mirroring the existing-journal path. This populates
                // animalStore.animal, which the header reads to show name/breed/age/weight.
                if (animalId) {
                    await animalStore.load(animalId);
                    // NOTE: We intentionally do NOT pre-fill journal.weight from the animal's
                    // last recorded weight. Auto-filling an old weight made new journals look
                    // like the animal had been weighed this visit, so the numbers couldn't be
                    // trusted (TICKET-01048). The weight field stays empty on new journals so
                    // the vet must actively enter today's weight; the last recorded weight is
                    // instead shown as a read-only reference in the animal header in render().
                    // (previously TICKET-00964)
                }
            }
        } else {
            const newJournals = await journalStore.load(parseInt(id, 10), false, { skipUpdate: true });
            journalStore.updateKeyValue('newJournal', newJournals[0]);
            // Baseline for diff saves: this is the server state this drawer session
            // started from. saveJournal() only sends fields that differ from it.
            journalStore.setNewJournalBaseline(newJournals[0]);
            if (newJournals[0] && newJournals[0].id) {
                // Pending partner (AI scribe) suggestions render in the existing AI cards.
                journalStore.applyPartnerSuggestions(newJournals[0]);
            }
            const newJournal = newJournals[0];
            if (newJournal) {
                // Track journal view (editing existing journal)
                if (newJournal.id) {
                    appState.trackJournal('view', newJournal);
                }

                const { animal } = newJournal;
                await animalStore.load(animal);
                if (newJournal.id) {
                    await this.loadJournals([animal], newJournal.id);
                }
                const { animal: currentAnimal } = animalStore;

                if (currentAnimal) {
                    const { owner } = currentAnimal;
                    await visitorStore.load(owner);
                }
            }
        }
    }

    loadJournals = async (animalIds, journalId) => {
        if (!animalIds || animalIds.length === 0) {
            return;
        }
        console.log('loadJournals', { animalIds, journalId });
        const { journalStore, saleStore } = this.props.stores;
        const query = {
            animal: animalIds,
        };
        const animalJournals = await journalStore.load({
            query,
            limit: 100,
            addData: ['animals', 'owners'],
            skipUpdate: true,
        });
        journalStore.updateKeyValue('animalJournals', animalJournals);
    }

    saveJournal = async e => {
        this.setState({
            loading: true,
        });
        const { saveSale = () => {}, saleDate } = this.props;
        const { journalStore, userStore, appState } = this.props.stores;
        const { user } = userStore
        const { newJournal } = journalStore;
        const hasSale = newJournal.sale;
        // Default the treating vet to the current user only when CREATING a new
        // journal. When editing an existing (often imported) journal, respect an
        // empty vet so it can be left blank without the app stamping whoever
        // opened it as "behandlende". (TICKET-01121)
        if (!newJournal.id && !newJournal.veterinary) {
            journalStore.updateObjectKeyValue('newJournal', 'veterinary', user.id);
        }
        // if (!hasSale) {
        //     this.setState({
        //         loading: false,
        //     });
        //     return false;
        // }

        if (newJournal.id) {
            const saveJournal = { ...newJournal };
            // Do not save arrays directly, they are handled separately
            delete saveJournal.images;
            // Only send fields changed since this drawer session loaded the journal —
            // a stale copy must never overwrite text another user/window saved in the
            // meantime (TICKET-01303). Falls back to the full object when no baseline
            // exists for this journal id.
            const payload = diffJournalForSave(saveJournal, journalStore.newJournalBaseline);
            // Persist a deliberately-cleared treating vet as 0. An empty string is
            // dropped by the backend Number cast (tc.asNumber('') -> undefined),
            // which would silently keep the previous vet. (TICKET-01121) Only coerce
            // when the vet field is part of this save — an untouched vet on a stale
            // copy must not clear one set by another session.
            if ('veterinary' in payload && !payload.veterinary) {
                payload.veterinary = 0;
            }
            await journalStore.savePreparedJournal(payload);
            appState.trackJournal('update', newJournal);
        } else {
            // TICKET-01394: a "Lagre"-button click (e is the click event) while the
            // shared newJournal has neither an id nor any user content must not POST.
            // The shared observable is blanked whenever another journal drawer mounts
            // (createNewJournal in loadAll), so a Lagre click in the first drawer then
            // inserted a stamped-but-empty DUPLICATE journal on the sale — the vet's
            // text was already autosaved field-by-field into the real journal while its
            // id was still present. Programmatic callers (createJournal, the
            // prescription pre-save that needs an id before attaching) pass no event
            // and insert exactly as before.
            if (e && !journalHasUserContent(newJournal)) {
                this.setState({ loading: false });
                return true;
            }
            const response = await journalStore.insert(newJournal);
            // Update current newJournal object
            const journal = response?.data?.journal;
            journalStore.updateObjectKeyValue('newJournal', 'id', journal?.id);
            journalStore.updateObjectKeyValue('newJournal', 'journalNumber', journal?.journalNumber);
            // Baseline for subsequent diff saves = what the server just accepted.
            // Captured BEFORE the saleDate tweak below so a changed date still gets
            // sent by the next save.
            journalStore.setNewJournalBaseline(journalStore.newJournal);
            if (saleDate) {
                journalStore.updateObjectKeyValue('newJournal', 'date', saleDate);
            }
            appState.trackJournal('create', journal);
        }
        await saveSale({ calledFrom: 'newJournal.saveJournal' });
        this.setState({
            loading: false,
        });
        return true;
    }

    // After «Send til DyreID» in the bottom bar: pull the journal back from the server so
    // the diagnosis cards and the button pick up dyreIdRegisteredId (TICKET-01752).
    // Mirrors the fresh-load path in loadJournal() so the diff-save baseline stays in sync.
    reloadJournalAfterDyreIdReport = async () => {
        const { journalStore } = this.props.stores;
        const { newJournal = {} } = journalStore;
        if (!(newJournal.id > 0)) {
            return;
        }
        const reloaded = await journalStore.load(newJournal.id, false, { skipUpdate: true });
        if (reloaded?.[0]?.id) {
            journalStore.updateKeyValue('newJournal', reloaded[0]);
            journalStore.setNewJournalBaseline(reloaded[0]);
        }
    }

    closeJournal = () => {
        // Open a prominent, centered in-app confirmation modal instead of the native
        // browser confirm() that pops up as an easy-to-miss little bar at the top of the
        // page (TICKET-01156). The real close logic runs in confirmCloseJournal().
        this.setState({ showCloseConfirm: true });
    }

    cancelCloseJournal = () => {
        this.setState({ showCloseConfirm: false });
    }

    confirmCloseJournal = async () => {
        this.setState({ showCloseConfirm: false });
        const {
            drawerLevel = 1,
        } = this.props;
        const { journalStore, saleStore, appState } = this.props.stores;

        const { newJournal } = journalStore;
        journalStore.updateObjectKeyValue('newJournal', 'closed', new Date());
        await journalStore.saveField(newJournal.id, 'closed', new Date());
        appState.trackJournal('close', newJournal);

        // await this.saveJournal();
        const { callback = () => {} } = this.props;
        await callback(newJournal);

        // When the sale this journal belongs to is already open underneath the drawer
        // (the besøk flow — you opened the journal from the visit you are billing),
        // just closing the drawer leaves you on the regningsfelt for this exact patient,
        // so keep the original behavior and avoid a redundant reload.
        const saleId = parseInt(newJournal.sale, 10);
        const alreadyOnSale = saleId > 0 && saleStore?.newSale?.id === saleId;
        if (!alreadyOnSale) {
            // Otherwise (e.g. editing from the Journaler list) jump straight to the linked
            // sale's billing screen (regningsfelt) with the owner + animal loaded, so the
            // vet can add product lines right after closing instead of hunting through the
            // visit list. gotoLinkedSale returns false when there is no linked sale (or a
            // clinic switch was declined) — fall back to just closing the drawer.
            const navigated = await this.gotoLinkedSale();
            if (navigated) {
                return true;
            }
        }
        appState.toggleDrawerRight(false, drawerLevel);
        return true;
    }

    createJournal = async () => {
        this.setState({
            loading: true,
        });
        const { callback = () => {} } = this.props;
        const { journalStore } = this.props.stores;
        await this.saveJournal();
        const { newJournal } = journalStore;
        callback(newJournal, true);
        this.setState({
            loading: false,
        });
    }

    onImageClick = e => {
        e.stopPropagation();
        e.preventDefault();
        const { image } = e.target.dataset;
        const {
            drawerLevel = 1,
        } = this.props;
        const { appState } = this.props.stores;

        appState.openDrawerRight('viewImage', {
            image,
            callback: async () => {
                // console.log('onClickEditJournal callback');
            },
        }, drawerLevel + 1);
    }

    onKeyDown = e => {
        // console.log('onKeyDown', e);
        // const { value } = e.target;
        // // Remove old timer
        // if (this.aiTimer) {
        //     clearTimeout(this.aiTimer);
        // }
        // this.aiTimer = setTimeout(() => {
        //     this.aiLookup(value);
        // }, 1000);
    }

    aiLookup = async message => {
        const { journalStore } = this.props.stores;
        await journalStore.queryAi(message);
    }

    loadAnimalJournals = async (animalIds, journalIds) => {
        console.log('loadJournals', { animalIds, journalIds });
        const { journalStore, saleStore } = this.props.stores;
        if (animalIds?.length > 0) {
            const query = {
                animal: animalIds,
            };
            if (journalIds?.length > 0) {
                query.id = journalIds;
            }
            const response = await journalStore.load({
                query,
                skipUpdate: true,
            });
            saleStore.updateKeyValue('animalJournals', response);
        } else {
            saleStore.updateKeyValue('animalJournals', []);
        }
    }

    toggleOtherJournals = () => {
        this.setState(prevState => ({ showOtherJournals: !prevState.showOtherJournals }));
    }

    addMedicineCallback = async (object, updateValueFunc, holdingStore) => {
        const { id, isNew } = this.props;
        const { saleStore } = this.props.stores;
        const { newSale } = saleStore;

        const animalIds = newSale.animals.map(a => a.id);
        const journalIds = newSale.journals.map(e => e.id);

        console.log('addMedicineCallback', { object, animalIds, journalIds });
        // await this.loadAnimalJournals([object?.animal], [object?.id]);
        await this.loadJournals([object?.animal], object?.id);

        // Don't reload the journal - it's already been updated in addMedicine/addDiagnose
        // await this.loadJournal(object.id, isNew);
    }

    onClickPrintJournal = () => this.printJournal();

    // TICKET-01660: owner-facing «Hjemsendelsesråd» sheet without the clinical notes.
    onClickPrintClientAdvice = () => this.printJournal({ adviceOnly: true });

    printJournal = ({ adviceOnly = false } = {}) => {
        const { userStore, journalStore, appState } = this.props.stores;
        const { user } = userStore;
        const { language = 'no' } = user;
        const languageDef = countryMap[language || 'default'];
        const { newJournal = {} } = journalStore;
        appState.trackJournal('print', newJournal, adviceOnly ? { adviceOnly: true } : {});

        // Render Receipt component with data
        const receipt = <IntlProvider definition={languageDef}>
            <DyrejournalJournalPrint stores={this.props.stores} journals={[newJournal]} adviceOnly={adviceOnly} />
        </IntlProvider>;

        // Append to document temporarily to render
        const tempDiv = document.createElement('div');
        tempDiv.className = 'receipt';
        document.body.appendChild(tempDiv);
        render(receipt, tempDiv);
        const htmlContent = tempDiv.outerHTML;
        document.body.removeChild(tempDiv);
        render(null, tempDiv);
        printViaIframe(htmlContent);
    }

    openJournalSettings = () => {
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        appState.openDrawer('journalSettings', {}, drawerLevel + 1);
    }

    onClickEditVisitor = e => {
        e.stopPropagation();
        e.preventDefault();
        const { drawerLevel = 1 } = this.props;
        const { appState, saleStore, visitorStore } = this.props.stores;
        const { drawerHeightLarge } = appState;
        const { visitor } = visitorStore;
        if (!visitor?.id) {
            return;
        }
        appState.openDrawer('editVisitor', {
            height: drawerHeightLarge,
            isNew: false,
            id: visitor.id,
            callback: () => {
                const { visitor: updatedVisitor } = visitorStore;
                const { newSale, visitorResults } = saleStore;
                const { visitors = [] } = newSale;
                const idx = visitors.findIndex(a => a.id === updatedVisitor.id);
                if (idx > -1) {
                    saleStore.updateObjectKeyArrValue('newSale', 'visitors', idx, updatedVisitor);
                }
                const idx2 = visitorResults.findIndex(a => a.id === updatedVisitor.id);
                if (idx2 > -1) {
                    saleStore.updateKeyArrValue('visitorResults', idx2, updatedVisitor);
                }
            },
        }, drawerLevel + 1);
    }

    persistAnimalChipId = () => {
        if (this.animalChipIdSaveTimer) {
            clearTimeout(this.animalChipIdSaveTimer);
            this.animalChipIdSaveTimer = null;
        }
        if (this.pendingAnimalChipId === null) {
            return this.animalChipIdSavePromise;
        }
        const { journalStore } = this.props.stores;
        const journalId = journalStore.newJournal?.id;
        if (!journalId) {
            return this.animalChipIdSavePromise;
        }
        const value = this.pendingAnimalChipId;
        this.pendingAnimalChipId = null;
        this.animalChipIdSavePromise = this.animalChipIdSavePromise
            .catch(() => null)
            .then(() => saveJournalAnimalChipId({ journalStore, journalId, value }));
        return this.animalChipIdSavePromise;
    }

    editAnimalChipId = e => {
        const { value } = e.target;
        const { journalStore, animalStore } = this.props.stores;
        const animalId = animalStore.animal?.id || journalStore.newJournal?.animal;
        stageJournalAnimalChipId({ journalStore, animalStore, animalId, value });
        this.pendingAnimalChipId = value;
        if (this.animalChipIdSaveTimer) {
            clearTimeout(this.animalChipIdSaveTimer);
        }
        this.animalChipIdSaveTimer = setTimeout(this.persistAnimalChipId, 700);
    }

    componentDidMount() {
        {
            const { journalStore, userStore } = this.props.stores;
            journalStore.loadPartnerLaunchers(userStore.customer ? userStore.customer.id : 0);
        }
        this.loadAll();
        this.syncJournalSubscription = PubSub.subscribe(topics.SYNC_JOURNAL_UPDATED, (data) => {
            const { journalStore } = this.props.stores;
            const { newJournal } = journalStore;
            // Only show banner if this specific journal was updated
            if (newJournal?.id && (!data.journalId || data.journalId === newJournal.id)) {
                this.setState({ syncJournalNotification: data });
            }
        });
    }

    // TICKET-01726: a headed AI-scribe note (Vetnio etc.) pasted into any medical
    // textarea is fanned out to the matching journal fields, verbatim. Capture phase
    // runs before the field's own paste handling; ordinary pastes fall through.
    handleJournalPaste = async (e) => {
        const target = e.target;
        if (!target || target.tagName !== 'TEXTAREA') {
            return;
        }
        const { clipboardData } = e;
        if (!clipboardData || (clipboardData.files && clipboardData.files.length > 0)) {
            return;
        }
        const text = clipboardData.getData('text/plain');
        const { journalStore } = this.props.stores;
        const { newJournal } = journalStore;
        const plan = planJournalPaste(text, newJournal, target.name);
        if (!plan) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        const fieldNames = Object.keys(plan);
        for (let i = 0; i < fieldNames.length; i += 1) {
            const field = fieldNames[i];
            const value = plan[field];
            journalStore.updateObjectKeyValue('newJournal', field, value);
            if (newJournal.id) {
                // eslint-disable-next-line no-await-in-loop
                await journalStore.saveField(newJournal.id, field, value);
            }
        }
    }

    componentWillUnmount() {
        this.props.stores.journalStore.clearPartnerSuggestions();
        this.persistAnimalChipId();
        if (this.syncJournalSubscription) {
            PubSub.unsubscribe(topics.SYNC_JOURNAL_UPDATED);
        }
    }

    render() {
        const { userStore, journalStore, saleStore, animalStore, visitorStore } = this.props.stores;
        // const { animal } = animalStore;
        const {
            week,
            isNew,
            hideAdmin = true,
            drawerLevel,
            saveSale = () => {},
            saleIsLocked = false,
            animals = [],
        } = this.props;
        const { sections, sectionsClinical, sectionsMedical, sectionsPassport, sectionsMedia, sectionsExtra, loading, currentCalendarEvent, showOtherJournals, showCloseConfirm, restoredDraftSavedAt } = this.state;
        const { user, isAdmin, isCustomerAdmin, customer } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');
        const weightViewMode = util.getNestedValue(user, 'settings.weightViewMode') || 'graph';
        const { featureFlags = {} } = customer;
        const useFormFieldsCompact = parseInt(featureFlags?.useFormFieldsCompact, 10) === 1;
        const hasAudioTranscribe = parseInt(featureFlags?.audioTranscribe, 10) === 1;
        const hasAmbientJournal = parseInt(featureFlags?.ambientJournal, 10) === 1;

        const { newJournal = {} } = journalStore;
        const { newSale } = saleStore;
        const { prescriptions } = newJournal;
        // «Send til DyreID» in the bottom bar (TICKET-01752): same gate as the DyreId
        // column in Alle besøk, plus a saved journal on a sale with a coded diagnosis.
        const hasCodedDiagnosis = (newJournal.diagnoses || []).some(d => d.uuidv4 && d.diagnoseCode);
        const showDyreIdReport = !!(featureFlags?.dyreIdReport && customer.dyreIdClinicId
            && newJournal.id > 0 && newJournal.sale > 0 && hasCodedDiagnosis);
        const { journals, animals: journalAnimals, animalJournals } = journalStore;
        const { animal: currentAnimal = {} } = animalStore;
        const animal = journalAnimals.find(a => a.id === newJournal.animal) || currentAnimal;

        // Last recorded weight, shown as a read-only reference on NEW journals so the vet
        // has context without it being pre-filled into the field (TICKET-01048). Use the
        // most-recent-dated allWeights entry, falling back to the animal's weight field.
        const allWeights = Array.isArray(currentAnimal.allWeights) ? currentAnimal.allWeights : [];
        const lastWeightEntry = allWeights.length > 0
            ? [...allWeights].sort(sortByDateAsc)[allWeights.length - 1]
            : null;
        const lastWeight = lastWeightEntry ? lastWeightEntry.weight : (currentAnimal.weight || null);
        const lastWeightDate = lastWeightEntry ? lastWeightEntry.date : null;

        const finalAnimals = animals.length > 0 ? animals : [animal];

        const { visitor } = visitorStore;
        const { journalItems = {} } = customer;

        // TICKET-01482: fill [animal]/[weight]/[name]/[clinic] codes when a
        // text template is inserted into the journal. Resolved at insert time
        // (not render time) so the freshest store values win.
        const templateFields = () => {
            const { newJournal: currentJournal = {} } = journalStore;
            return buildJournalTemplateFields({
                journal: currentJournal,
                animal: journalStore.animals.find(a => a.id === currentJournal.animal) || animalStore.animal,
                visitor: visitorStore.visitor,
                customer: userStore.customer,
            });
        };
        const hideFields = Object.keys(journalItems).filter(key => journalItems[key] === 1).map(key => ({ name: key }));
        const basicsHideFields = [...hideFields, { name: 'comment' }];

        const gateAudioTranscribe = (secs) => secs.map(s => ({
            ...s,
            fields: s.fields.map(f => f.audioTranscribe ? { ...f, audioTranscribe: hasAudioTranscribe } : f),
        }));

        const { syncJournalNotification } = this.state;

        return (
            <div class='w-100 d-flex justify-content-center' style='overflow-x: hidden; max-width: 100vw;'>
                <div class='w-100 h-100 position-relative' style='max-width: 960px; padding-top: 40px; padding-bottom: 100px; overflow-x: hidden;'>

                    {syncJournalNotification && <div class='mx-3 mb-2'>
                        <SyncBanner
                            type='journal'
                            detail={true}
                            updatedByName={syncJournalNotification.updatedByName}
                            onDismiss={() => this.setState({ syncJournalNotification: null })}
                        />
                    </div>}

                    {restoredDraftSavedAt > 0 && <div class='mx-3 mb-2'>
                        <div class='alert alert-info d-flex align-items-center justify-content-between py-2 mb-0'>
                            <span>
                                <i class='fa-duotone fa-clock-rotate-left me-2' />
                                <Text id='dyrejournal.journal.draft-restored'>Unsaved journal text was restored</Text>
                                {' '}
                                ({util.formatDate(restoredDraftSavedAt, { hour12: false, hour: '2-digit', minute: '2-digit', locale: 'nb-NO' })})
                            </span>
                            <button
                                type='button'
                                class='btn btn-sm btn-link text-body p-0 border-0'
                                onClick={() => this.setState({ restoredDraftSavedAt: null })}
                            >
                                <i class='fa-solid fa-xmark' />
                            </button>
                        </div>
                    </div>}

                    <Versions stores={this.props.stores} type='journal' id={newJournal.id} key={`journal-${newJournal.id}`} />

                    {isCustomerAdmin && <div class='position-absolute' style='top: 5px; right: 10px; z-index: 10;'>
                        <button type='button' class='btn btn-sm btn-outline-secondary rounded-circle'
                            onClick={this.openJournalSettings}>
                            <i class='fa-solid fa-gear' />
                        </button>
                    </div>}
                    <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between align-items-center'>
                        <span>
                            {isNew ? <Text id='dyrejournal.journal.new'>New Journal</Text> : <Text id='dyrejournal.journal.edit'>Edit Journal</Text>}
                            {newJournal.id && <>: {newJournal.id}</>}
                        </span>
                        {newJournal.sale > 0 && (
                            <button
                                type='button'
                                class='btn btn-sm btn-success rounded-pill ms-2'
                                onClick={this.onClickGotoSale}
                            >
                                <i class='fa-duotone fa-right-to-bracket me-1' />
                                <Text id='dyrejournal.journal.to-sale'>To sale</Text>
                                {newJournal.saleNumber > 0 && <> {newJournal.saleNumber}</>}
                            </button>
                        )}
                    </div>

                    <DyrejournalLinkJournalToSale
                        stores={this.props.stores}
                        journal={newJournal}
                        animal={animal}
                        onLinked={(targetSale, action) => {
                            if (action === 'unlink') {
                                // Only clear the header "To sale" reference if the
                                // removed link was the sale it currently points to.
                                if (parseInt(newJournal.sale, 10) === targetSale.id) {
                                    journalStore.updateObjectKeyValue('newJournal', 'sale', 0);
                                    journalStore.updateObjectKeyValue('newJournal', 'saleNumber', 0);
                                }
                                return;
                            }
                            journalStore.updateObjectKeyValue('newJournal', 'sale', targetSale.id);
                            journalStore.updateObjectKeyValue('newJournal', 'saleNumber', targetSale.saleNumber);
                        }}
                    />

                    <div class='mx-3 mt-4 px-3 py-2 rounded bg-body-secondary'>
                        <div class='d-flex flex-row align-items-center flex-wrap'>
                            <div class='flex-fill'>
                                <i class='fa-duotone fa-paw me-1 text-body-secondary' />
                                <strong>{currentAnimal.name}</strong>
                                {(currentAnimal.species || currentAnimal.breed) && <span class='text-body-secondary ms-1'>({currentAnimal.species}{currentAnimal.breed ? `/${currentAnimal.breed}` : ''})</span>}
                                {currentAnimal.birth && <span class='text-body-secondary ms-1'>&middot; {animalAge(currentAnimal.birth)}</span>}
                            </div>
                            {visitor && visitor.id && <div class='ms-2'>
                                <button
                                    type='button'
                                    class='btn btn-sm btn-link text-body p-0 border-0 align-baseline text-decoration-none'
                                    onClick={this.onClickEditVisitor}
                                    title='Edit owner'
                                >
                                    <i class='fa-duotone fa-user me-1 text-body-secondary' />
                                    <span>{visitor.firstname ? `${visitor.firstname} ${visitor.lastname}` : visitor.name}</span>
                                    {visitor.cellphone && <span class='text-body-secondary ms-1'>&middot; {visitor.cellphone}</span>}
                                    <i class='fa-duotone fa-pen-to-square ms-1 text-body-secondary' />
                                </button>
                            </div>}
                            {(animalJournals && animalJournals.length > 0 && drawerLevel <= 3) && <div class='ms-2'>
                                <button
                                    type='button'
                                    class={`btn btn-sm btn-outline-secondary rounded-pill ms-3`}
                                    onClick={this.toggleOtherJournals}
                                >
                                    <i class={`fa-duotone fa-clipboard-list me-1`} />
                                    <Text id='dyrejournal.journal.other-journals'>Journals</Text> ({animalJournals.length})
                                    <i class={`fa-solid fa-chevron-${showOtherJournals ? 'up' : 'down'} ms-1`} />
                                </button>
                            </div>}
                        </div>
                        {/* TICKET-01448: the animal's "Viktig informasjon" only lived on the animal
                            card on the visit screen (listAnimalMini.js), which this drawer covers,
                            so the vet lost it the moment the journal was opened. Repeat it in the
                            journal header. Deliberately NOT added to journalPrint.js or the backend
                            journal PDF/email — this is internal clinic info, not for the owner. */}
                        {currentAnimal.importantInfo && <div class='mt-2 px-2 py-1 rounded bg-info text-white' style='font-size: 0.85em;'>
                            <i class='fa-solid fa-info-circle me-1' />
                            <strong><Text id='dyrejournal.important-info'>Important info</Text>:</strong> {currentAnimal.importantInfo}
                        </div>}
                        {currentAnimal.insuranceId && <div class='text-body-secondary mt-1' style='font-size: 0.85em;'>
                            <i class='fa-duotone fa-shield-check me-1' />
                            {currentAnimal.insuranceId} {currentAnimal.insuranceCompany ? `(${currentAnimal.insuranceCompany})` : ''}
                        </div>}
                        <div class='d-flex flex-row align-items-center mt-2' style='max-width: 360px;'>
                            <div class='input-group input-group-sm'>
                                <span class='input-group-text'>
                                    <i class='fa-regular fa-microchip me-1' />
                                    <Text id='input.chipId'>Chip ID</Text>
                                </span>
                                <Localizer>
                                    <input
                                        type='text'
                                        inputmode='numeric'
                                        autocomplete='off'
                                        class='form-control'
                                        placeholder={<Text id='input.chipId'>Chip ID</Text>}
                                        value={currentAnimal.chipId || ''}
                                        onInput={this.editAnimalChipId}
                                        onBlur={this.persistAnimalChipId}
                                    />
                                </Localizer>
                            </div>
                        </div>
                        {isNew && !!lastWeight && <div class='mt-2 px-2 py-1 rounded bg-body border' style='font-size: 0.85em;'>
                            <div>
                                <i class='fa-duotone fa-weight-scale me-1 text-body-secondary' />
                                <Text id='dyrejournal.last-recorded-weight'>Last recorded weight</Text>:
                                {' '}<strong>{lastWeight} kg</strong>
                                {!!lastWeightDate && <span class='text-body-secondary ms-1'>
                                    ({new Date(lastWeightDate).toLocaleString('nb-NO', { day: '2-digit', month: 'short', year: '2-digit' })})
                                </span>}
                            </div>
                            <div class='text-body-secondary fst-italic mt-1'>
                                <Text id='dyrejournal.weigh-reminder'>For reference only — remember to weigh the animal and enter today's weight in the field below.</Text>
                            </div>
                        </div>}
                        {currentAnimal.allWeights && currentAnimal.allWeights.length > 2 && <>
                            <div class='d-flex flex-row justify-content-end px-1 py-0 mt-1'>
                                <button
                                    class='btn btn-sm btn-link text-body-secondary py-0 px-1'
                                    onClick={this.toggleWeightView}
                                    title={weightViewMode === 'graph' ? 'Show table' : 'Show graph'}
                                >
                                    <i class={`fa-solid ${weightViewMode === 'graph' ? 'fa-table' : 'fa-chart-line'}`} />
                                </button>
                            </div>
                            {weightViewMode === 'graph' ? (
                                <div class='d-flex flex-row justify-content-end px-1 py-0'>
                                <LineGraphMini
                                    stores={this.props.stores}
                                    dataLeft={[currentAnimal.allWeights.sort(sortByDateAsc).map(d => ({
                                        epoch: Math.floor(new Date(d.date).getTime()),
                                        x: Math.floor(new Date(d.date).getTime()),
                                        y: d.weight
                                    }))]}
                                    legendLeft={['Vekt']}
                                    unitLeft={['kg']}
                                    yTicksLeftVal={val => `${val} kg`}
                                    xTicksVal={val => new Date(val).toLocaleString('nb-NO', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: '2-digit',
                                    })}
                                    width={currentAnimal.allWeights.length > 10 ? 600 : 300}
                                    height={50}
                                    smoothLeft={[true]}
                                    gradientFillLeft={['#0d9488']}
                                    gradientOpacity={0.15}
                                    inputStyleLeft={'stroke: #0d9488 !important; stroke-width: 2px !important;'}
                                    showValueLeftLine={true}
                                />
                                </div>
                            ) : (
                                <div class='px-1' style='line-height: 1.2; max-height: 100px; overflow-y: auto;'>
                                    <table class='table table-sm mb-0' style='font-size: 0.75rem;'>
                                        <thead>
                                            <tr>
                                                <th class='fw-normal text-body-secondary py-0'><Text id='dyrejournal.weight-date'>Date</Text></th>
                                                <th class='fw-normal text-body-secondary text-end py-0'><Text id='dyrejournal.weight-kg'>Weight (kg)</Text></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[...currentAnimal.allWeights].sort(sortByDateAsc).reverse().map(w => (
                                                <tr>
                                                    <td class='py-0'>{new Date(w.date).toLocaleString('nb-NO', {
                                                        day: '2-digit',
                                                        month: 'short',
                                                        year: '2-digit',
                                                    })}</td>
                                                    <td class='text-end py-0'>{w.weight} kg</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>}
                    </div>
                    {showOtherJournals && animalJournals && animalJournals.length > 0 && drawerLevel <= 3 && <DyrejournalJournalListMini
                        classNames='mx-3 mt-0 px-2 py-2 box-container'
                        stores={this.props.stores}
                        journals={animalJournals}
                        drawerLevel={drawerLevel}
                        collapse={false}
                        viewOnly={true}
                    />}

                    {hasAmbientJournal && <ConsultationRecorder stores={this.props.stores} />}
                    <div class='d-flex flex-column justify-content-start' style='padding: 0 8px;'>
                        {useFormFieldsCompact ? (
                            <FormFieldsCompact
                                stores={this.props.stores}
                                sections={sections}
                                templateFields={templateFields}
                                holdingStore={journalStore}
                                objectName={'newJournal'}
                                isNew={newJournal.id ? false : true}
                                hideAdmin={hideAdmin}
                                drawerLevel={drawerLevel}
                                hideFields={basicsHideFields}
                                defaultShowOnlyRecommended={true}
                            />
                        ) : (
                            <FormFields
                                stores={this.props.stores}
                                sections={gateAudioTranscribe(sections)}
                                holdingStore={journalStore}
                                objectName={'newJournal'}
                                isNew={newJournal.id ? false : true}
                                hideAdmin={hideAdmin}
                                drawerLevel={drawerLevel}
                                hideFields={basicsHideFields}
                            />
                        )}
                    </div>

                    <div class='mx-2 mt-3 mb-4 p-3 rounded border-start border-primary bg-body-tertiary' style='border-left-width: 4px !important; background-color: rgba(0,0,0,0.06) !important;'>
                        <div class='fw-semibold mb-2'>
                            <i class='fa-duotone fa-prescription me-2 text-primary' />
                            <Text id='dyrejournal.journal.prescriptions'>Journal prescriptions</Text>
                        </div>
                        <DyrejournalPrescriptionListMini
                            classNames=''
                            stores={this.props.stores}
                            prescriptions={prescriptions}
                            saleIsLocked={saleIsLocked}
                            saveJournal={this.saveJournal}
                            drawerLevel={drawerLevel}
                            journal={newJournal.id}
                        />
                    </div>

                    <PartnerSuggestionsBanner stores={this.props.stores} journal={newJournal} />
                    <PartnerLaunchButtons stores={this.props.stores} journal={newJournal} />

                    {featureFlags?.idexx === 1 && newJournal.id > 0 && (
                        <div class='mx-2 mt-3 mb-4 p-3 rounded border-start border-primary bg-body-tertiary' style='border-left-width: 4px !important; background-color: rgba(0,0,0,0.06) !important;'>
                            <div class='fw-semibold mb-2'>
                                <i class='fa-duotone fa-flask-vial me-2 text-primary' />
                                <Text id='dyrejournal.idexx.labOrders'>IDEXX Lab Orders</Text>
                            </div>
                            <IdexxOrderStatus
                                stores={this.props.stores}
                                journal={newJournal}
                                animal={animal}
                                visitor={visitor}
                                drawerLevel={drawerLevel}
                            />
                        </div>
                    )}

                    {featureFlags?.idexx === 1 && newJournal.id > 0 && newJournal.animal > 0 && (
                        <div class='mx-2 mt-3 mb-4 p-3 rounded border-start border-primary bg-body-tertiary' style='border-left-width: 4px !important; background-color: rgba(0,0,0,0.06) !important;'>
                            <div class='fw-semibold mb-2'>
                                <i class='fa-duotone fa-vials me-2 text-primary' />
                                <Text id='dyrejournal.idexx.labResults'>IDEXX lab results</Text>
                            </div>
                            <DyrejournalLabResultsListMini
                                classNames=''
                                stores={this.props.stores}
                                animal={newJournal.animal}
                                journal={newJournal}
                                saleIsLocked={saleIsLocked}
                                drawerLevel={drawerLevel}
                            />
                        </div>
                    )}

                    <DentalChartSection
                        stores={this.props.stores}
                        journal={newJournal}
                        animal={animal}
                        objectName='newJournal'
                        readOnly={saleIsLocked}
                    />

                    <div class='d-flex flex-column justify-content-start' style='padding: 0 8px;' onPasteCapture={this.handleJournalPaste}>
                        {useFormFieldsCompact ? (
                            <FormFieldsCompact
                                stores={this.props.stores}
                                sections={sectionsMedical}
                                templateFields={templateFields}
                                holdingStore={journalStore}
                                objectName={'newJournal'}
                                isNew={newJournal.id ? false : true}
                                hideAdmin={hideAdmin}
                                drawerLevel={drawerLevel}
                                onKeyDown={this.onKeyDown}
                                drawerCallback={this.addMedicineCallback}
                                hideFields={hideFields}
                            />
                        ) : (
                            <FormFields
                                stores={this.props.stores}
                                sections={gateAudioTranscribe(sectionsMedical)}
                                holdingStore={journalStore}
                                objectName={'newJournal'}
                                isNew={newJournal.id ? false : true}
                                hideAdmin={hideAdmin}
                                drawerLevel={drawerLevel}
                                onKeyDown={this.onKeyDown}
                                drawerCallback={this.addMedicineCallback}
                                hideFields={hideFields}
                            />
                        )}
                    </div>

                    <div class='d-flex flex-column justify-content-start' style='padding: 0 8px;'>
                        {useFormFieldsCompact ? (
                            <FormFieldsCompact
                                stores={this.props.stores}
                                sections={sectionsClinical}
                                templateFields={templateFields}
                                holdingStore={journalStore}
                                objectName={'newJournal'}
                                isNew={newJournal.id ? false : true}
                                hideAdmin={hideAdmin}
                                drawerLevel={drawerLevel}
                                onKeyDown={this.onKeyDown}
                                hideFields={hideFields}
                            />
                        ) : (
                            <FormFields
                                stores={this.props.stores}
                                sections={gateAudioTranscribe(sectionsClinical)}
                                holdingStore={journalStore}
                                objectName={'newJournal'}
                                isNew={newJournal.id ? false : true}
                                hideAdmin={hideAdmin}
                                drawerLevel={drawerLevel}
                                onKeyDown={this.onKeyDown}
                                hideFields={hideFields}
                            />
                        )}
                    </div>

                    <div class='d-flex flex-column justify-content-start' style='padding: 0 8px;'>
                        {useFormFieldsCompact ? (
                            <FormFieldsCompact
                                stores={this.props.stores}
                                sections={sectionsExtra}
                                templateFields={templateFields}
                                holdingStore={journalStore}
                                objectName={'newJournal'}
                                isNew={newJournal.id ? false : true}
                                hideAdmin={hideAdmin}
                                drawerLevel={drawerLevel}
                                hideFields={hideFields}
                            />
                        ) : (
                            <FormFields
                                stores={this.props.stores}
                                sections={sectionsExtra}
                                holdingStore={journalStore}
                                objectName={'newJournal'}
                                isNew={newJournal.id ? false : true}
                                hideAdmin={hideAdmin}
                                drawerLevel={drawerLevel}
                                hideFields={hideFields}
                            />
                        )}
                    </div>

                    {newJournal.images && newJournal.images.length > 0 && <>
                        <div class='w-100 d-flex flex-column' style='padding: 0 8px;'>
                            <div class='box-header-info d-flex flex-column'>
                                <div class='d-flex'>
                                    <ImageScroller
                                        images={newJournal.images}
                                        stores={this.props.stores}
                                        maxHeight={'60vh'}
                                        showImg={img => localUtil.displayImg(img, this.props, '1920x')}
                                        onImageClick={this.onImageClick}
                                    />
                                </div>
                            </div>
                        </div>
                    </>}

                    <div class='d-flex flex-column justify-content-start' style='padding: 0 8px;'>
                        {useFormFieldsCompact ? (
                            <FormFieldsCompact
                                stores={this.props.stores}
                                sections={sectionsMedia}
                                templateFields={templateFields}
                                holdingStore={journalStore}
                                objectName={'newJournal'}
                                isNew={newJournal.id ? false : true}
                                hideAdmin={hideAdmin}
                                drawerLevel={drawerLevel}
                                hideFields={hideFields}
                                defaultShowOnlyRecommended={true}
                            />
                        ) : (
                            <FormFields
                                stores={this.props.stores}
                                sections={gateAudioTranscribe(sectionsMedia)}
                                holdingStore={journalStore}
                                objectName={'newJournal'}
                                isNew={newJournal.id ? false : true}
                                hideAdmin={hideAdmin}
                                drawerLevel={drawerLevel}
                                hideFields={hideFields}
                            />
                        )}
                    </div>

                    {!isNew && newJournal.id > 0 && isCustomerAdmin
                        && parseInt(featureFlags?.journalFeedback, 10) === 1 && (
                        <JournalFeedback
                            stores={this.props.stores}
                            journal={newJournal}
                        />
                    )}

                    <div class='w-100 d-flex flex-row flex-wrap justify-content-between mt-3 px-2 px-sm-4 py-2 py-sm-3 bg-light fixed-bottom' style={`bottom: 0; left: 0; gap: 0.5rem;`}>
                        <button type='button' class={`btn btn-${newJournal.id ? 'success' : 'primary'} flex-fill rounded-pill`} style='min-width: 120px;' onClick={isNew ? this.createJournal : this.saveJournal} disabled={loading}>
                            <i class='fa-duotone fa-save me-2' />
                            {(isNew && !newJournal.id) ? <Text id='journal.create'>Create journal</Text> : <Text id='journal.save'>Save journal</Text>}
                            {loading && <i class='fa-regular fa-spinner-third fa-spin ms-2' />}
                        </button>
                        <button
                            class={`btn btn-info rounded-pill`}
                            style='min-width: 100px;'
                            onClick={this.onClickPrintJournal}
                        >
                            <i class='fa-solid fa-print' /> <span class='d-none d-sm-inline'><Text id='dyrejournal.print-journal'>Print journal</Text></span><span class='d-inline d-sm-none'><Text id='dyrejournal.print'>Print</Text></span>
                        </button>
                        <button
                            type='button'
                            class={`btn btn-outline-info rounded-pill`}
                            style='min-width: 100px;'
                            onClick={this.onClickPrintClientAdvice}
                            disabled={!hasClientAdvice(newJournal)}
                        >
                            <i class='fa-solid fa-print' /> <span class='d-none d-sm-inline'><Text id='dyrejournal.print-client-advice'>Print home care advice</Text></span><span class='d-inline d-sm-none'><Text id='dyrejournal.client-advice-title'>Home care advice</Text></span>
                        </button>
                        {showDyreIdReport && (
                            <DyreIdReportBatchButton
                                stores={this.props.stores}
                                saleId={newJournal.sale}
                                journalId={newJournal.id}
                                journals={[newJournal]}
                                customer={customer}
                                user={user}
                                buttonClass='btn btn-outline-primary rounded-pill'
                                onReported={this.reloadJournalAfterDyreIdReport}
                            />
                        )}
                        {(!isNew || newJournal.id) && <button type='button' class='btn btn-danger flex-fill rounded-pill' style='min-width: 120px;' onClick={this.closeJournal} disabled={loading}>
                            <i class='fa-duotone fa-lock me-2' />
                            <Text id='journal.close'>Close journal</Text>
                        </button>}
                    </div>

                    {showCloseConfirm && (
                        <div
                            class='modal d-block'
                            tabindex='-1'
                            style='background-color: rgba(0,0,0,0.6); z-index: 1060;'
                            onClick={this.cancelCloseJournal}
                        >
                            <div class='modal-dialog modal-dialog-centered' onClick={e => e.stopPropagation()}>
                                <div class='modal-content border border-4 border-danger shadow-lg' style='border-radius: 0;'>
                                    <div class='modal-header bg-danger text-white' style='border-radius: 0;'>
                                        <h4 class='modal-title mb-0'>
                                            <i class='fa-duotone fa-lock me-2' />
                                            <Text id='journal.closeConfirmTitle'>Close and lock journal?</Text>
                                        </h4>
                                        <button type='button' class='btn-close btn-close-white' aria-label='Close' onClick={this.cancelCloseJournal} />
                                    </div>
                                    <div class='modal-body py-4'>
                                        <p class='fs-5 mb-0'>
                                            <Text id='journal.closeConfirmBody'>Are you sure you want to close and lock the journal?</Text>
                                        </p>
                                    </div>
                                    <div class='modal-footer'>
                                        <button type='button' class='btn btn-lg btn-outline-secondary rounded-pill px-4' onClick={this.cancelCloseJournal}>
                                            <Text id='journal.closeConfirmCancel'>Cancel</Text>
                                        </button>
                                        <button type='button' class='btn btn-lg btn-danger rounded-pill px-4' onClick={this.confirmCloseJournal}>
                                            <i class='fa-duotone fa-lock me-2' />
                                            <Text id='journal.closeConfirmConfirm'>Close and lock</Text>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        );
    }
}

export default NewJournal;
